import { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import ProductCard from "@/components/store/ProductCard";
import ProductDetailModal from "@/components/store/ProductDetailModal";
import {
  getAllProducts,
  getAllCollections,
  getProductByHandle,
} from "@/lib/shopify";
import { ShoppingBag, X, Filter } from "lucide-react";

const ALL_COLLECTIONS = "__all__";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCollection, setActiveCollection] = useState(ALL_COLLECTIONS);
  const [activeTags, setActiveTags] = useState([]);
  const [openProduct, setOpenProduct] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Live fetch from Shopify's public products.json on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prods, cols] = await Promise.all([
          getAllProducts(),
          getAllCollections(),
        ]);
        if (cancelled) return;
        setProducts(prods);
        setCollections(cols);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load store");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // All tags across all products (deduped + sorted)
  const allTags = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filtered = useMemo(() => {
    let list = products;
    if (activeTags.length > 0) {
      list = list.filter((p) =>
        activeTags.every((t) => p.tags?.includes(t))
      );
    }
    return list;
  }, [products, activeTags]);

  const toggleTag = (tag) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setActiveCollection(ALL_COLLECTIONS);
    setActiveTags([]);
  };

  const handleSelectProduct = async (product) => {
    // Refetch by handle so we always have full description/variant data even
    // if the product was loaded from a thinner query in the future.
    try {
      const full = await getProductByHandle(product.handle);
      setOpenProduct(full || product);
    } catch {
      setOpenProduct(product);
    }
  };

  const hasActiveFilters =
    activeCollection !== ALL_COLLECTIONS || activeTags.length > 0;

  return (
    <>
      <SEO
        title="Store | Veterans for Indiana"
        description="Official Veterans for Indiana gear — shirts, hats, stickers, and more. All proceeds support our mission of electing veterans and military families across Indiana."
        path="/store"
      />

      {/* HERO */}
      <section className="bg-cream py-16">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Reveal>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-brand text-white">
              <ShoppingBag size={24} />
            </div>
            <p className="eyebrow mt-5">Official Gear</p>
            <h1 className="h-display text-5xl sm:text-6xl text-navy mt-2">Store</h1>
            <div className="mt-4 flex justify-center">
              <StarDivider />
            </div>
            <p className="mt-6 text-navy-deep/80 leading-relaxed">
              Show your support for Hoosier veterans. Every purchase fuels candidate recruitment, training, and direct campaign support.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORE GRID */}
      <section className="bg-white py-12">
        <div className="container-x">
          {/* Mobile filter toggle */}
          <div className="lg:hidden mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-display tracking-widest uppercase text-navy"
              data-testid="store-mobile-filter-toggle"
            >
              <Filter size={16} /> Filters
              {hasActiveFilters && (
                <span className="text-[10px] bg-green-brand text-white px-1.5 py-0.5 rounded-full">
                  {(activeCollection !== ALL_COLLECTIONS ? 1 : 0) + activeTags.length}
                </span>
              )}
            </button>
            <p className="text-sm text-navy-deep/65" data-testid="store-result-count">
              {filtered.length} {filtered.length === 1 ? "product" : "products"}
            </p>
          </div>

          <div className="grid lg:grid-cols-[260px,1fr] gap-10">
            {/* Sidebar — desktop */}
            <aside className="hidden lg:block">
              <FiltersPanel
                collections={collections}
                activeCollection={activeCollection}
                onCollection={setActiveCollection}
                allTags={allTags}
                activeTags={activeTags}
                onToggleTag={toggleTag}
                onClear={clearFilters}
                resultCount={filtered.length}
              />
            </aside>

            {/* Sidebar — mobile (drawer) */}
            {mobileFiltersOpen && (
              <div
                className="fixed inset-0 z-[70] lg:hidden flex"
                onClick={() => setMobileFiltersOpen(false)}
              >
                <div className="ml-auto h-full w-[320px] max-w-[85%] bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="p-5 flex items-center justify-between border-b border-gray-200">
                    <p className="font-display tracking-widest uppercase text-navy">Filters</p>
                    <button
                      type="button"
                      onClick={() => setMobileFiltersOpen(false)}
                      aria-label="Close filters"
                      className="h-9 w-9 rounded-md hover:bg-gray-soft inline-flex items-center justify-center text-navy"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="p-5">
                    <FiltersPanel
                      collections={collections}
                      activeCollection={activeCollection}
                      onCollection={setActiveCollection}
                      allTags={allTags}
                      activeTags={activeTags}
                      onToggleTag={toggleTag}
                      onClear={clearFilters}
                      resultCount={filtered.length}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Grid */}
            <div>
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse bg-gray-soft rounded-2xl aspect-[3/4]"
                    />
                  ))}
                </div>
              ) : error ? (
                <div className="rounded-xl ring-1 ring-red-200 bg-red-50 text-red-800 p-6" data-testid="store-error">
                  <p className="font-display tracking-widest uppercase text-sm">Store unavailable</p>
                  <p className="text-sm mt-2">{error}</p>
                </div>
              ) : filtered.length === 0 ? (
                <EmptyState hasActiveFilters={hasActiveFilters} onClear={clearFilters} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="store-product-grid">
                  {filtered.map((p) => (
                    <ProductCard
                      key={p.id}
                      product={p}
                      onSelect={handleSelectProduct}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {openProduct && (
        <ProductDetailModal
          product={openProduct}
          onClose={() => setOpenProduct(null)}
        />
      )}
    </>
  );
}

function FiltersPanel({
  collections,
  activeCollection,
  onCollection,
  allTags,
  activeTags,
  onToggleTag,
  onClear,
  resultCount,
}) {
  return (
    <div className="space-y-8">
      <div className="hidden lg:flex items-center justify-between">
        <p className="font-display tracking-widest uppercase text-navy text-sm">
          Filters
        </p>
        <span className="text-xs text-navy-deep/60" data-testid="store-result-count-desktop">
          {resultCount} {resultCount === 1 ? "result" : "results"}
        </span>
      </div>

      {/* Collections */}
      {collections.length > 0 && (
        <div>
          <p className="font-display tracking-widest uppercase text-xs text-navy mb-3">
            Collections
          </p>
          <ul className="space-y-1.5 text-sm">
            <li>
              <FilterButton
                active={activeCollection === ALL_COLLECTIONS}
                onClick={() => onCollection(ALL_COLLECTIONS)}
                testId="filter-collection-all"
              >
                All Products
              </FilterButton>
            </li>
            {collections.map((c) => (
              <li key={c.id}>
                <FilterButton
                  active={activeCollection === c.handle}
                  onClick={() => onCollection(c.handle)}
                  testId={`filter-collection-${c.handle}`}
                >
                  {c.title}
                </FilterButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tags */}
      {allTags.length > 0 && (
        <div>
          <p className="font-display tracking-widest uppercase text-xs text-navy mb-3">
            Tags
          </p>
          <div className="flex flex-wrap gap-1.5">
            {allTags.map((t) => {
              const active = activeTags.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => onToggleTag(t)}
                  className={`text-xs px-2.5 py-1 rounded-full ring-1 transition-colors ${
                    active
                      ? "bg-green-brand text-white ring-green-brand"
                      : "bg-white text-navy-deep/80 ring-gray-300 hover:ring-green-brand"
                  }`}
                  data-testid={`filter-tag-${t}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onClear}
        className="text-xs font-display tracking-widest uppercase text-green-brand hover:text-navy"
        data-testid="filter-clear"
      >
        Clear filters
      </button>
    </div>
  );
}

function FilterButton({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left px-2.5 py-1.5 rounded-md transition-colors ${
        active
          ? "bg-navy text-white"
          : "text-navy-deep/80 hover:bg-gray-soft hover:text-navy"
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ hasActiveFilters, onClear }) {
  return (
    <div
      className="rounded-2xl ring-1 ring-gray-200 bg-cream/60 p-12 text-center"
      data-testid="store-empty-state"
    >
      <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-white ring-1 ring-gray-200 text-navy">
        <ShoppingBag size={22} />
      </div>
      <p className="mt-5 font-display tracking-widest uppercase text-navy text-sm">
        {hasActiveFilters ? "No products match these filters" : "No products yet"}
      </p>
      <p className="mt-2 text-sm text-navy-deep/70">
        {hasActiveFilters
          ? "Try removing a filter to see more gear."
          : "Official Veterans for Indiana gear is on the way — check back soon."}
      </p>
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClear}
          className="btn btn-secondary mt-6"
          data-testid="store-empty-clear"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
